/**
 * ContactPage — rebuilt to mirror japantollreceipts.jp/en/contact.
 *
 * Layout:
 *   - Compact header ("Contact Us")
 *   - Two-column main area:
 *       Left  → Phone · Email · Office Location · Business Hours
 *       Right → Send Us a Message form (Name, Email, Message)
 *   - Below: Office location heading + Google Maps embed (street-level)
 *   - Bottom: "Before you write — most asked questions" FAQ list
 *
 * Form behavior: submits to POST /api/contact (real backend — persists
 * to Mongo + sends a Mandrill notification/acknowledgment email). On
 * success the form renders an in-page success card.
 *
 * Address: NoJo Enterprise, Co. Ltd. · #202 Takamura building ·
 *          2-2-1 Honcho, Yokosuka 238-0041
 */
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  ArrowUpRight,
  CheckCircle2,
  ChevronDown,
  Clock,
  Mail,
  MapPin,
  Phone,
  RotateCcw,
  Send,
} from "lucide-react";

import SEO, { PAGE_SEO } from "@/components/SEO";
import PageSideAura from "@/components/motion/PageSideAura";
import JsonLd, { breadcrumbSchema } from "@/components/JsonLd";
import Reveal from "@/components/motion/Reveal";

const PURPLE = "#5B2E83";
const GREEN = "#2F8F3C";
const BLUE = "#0E5FB0";

// Office coordinates — Yokosuka HQ
// Verified against the owner-supplied Street View pano (the exact view
// shown on the contact page). These coordinates match the
// `znkdVMmk13kaGn_VKMUTeQ` panorama, which centers on the Takamura
// building (高村ビル) on Honcho-dori — the office is on the 2nd floor.
const OFFICE = {
  nameEn: "NoJo Enterprise, Co. Ltd.",
  nameJa: "株式会社 NoJo Enterprise",
  addr1En: "#202 Takamura building",
  addr1Ja: "高村ビル 202号室",
  addr2En: "2-2-1 Honcho, Yokosuka 238-0041",
  addr2Ja: "〒238-0041 神奈川県横須賀市本町 2-2-1",
  lat: 35.2822804,
  lng: 139.665698,
};

const MAPS_LINK = `https://www.google.com/maps/search/?api=1&query=${OFFICE.lat},${OFFICE.lng}`;
// Google Maps Embed — locked Street View pano (no API key required).
//
// We use the `/maps/embed?pb=...` format (rather than the older
// `output=svembed` format) because `pb=` lets us pin BOTH the exact
// panorama ID and the initial yaw/pitch/zoom — guaranteeing every
// visitor lands on the same view of the office facade.
//
// Owner-verified view (from Google Maps share URL, Feb 2026):
//   pano    znkdVMmk13kaGn_VKMUTeQ  (Honcho-dori, front of HQ building)
//   heading 135.94°                  (south-east, toward the office entrance)
//   pitch   -9.04°                   (eye level, very slight downward tilt)
//   zoom    0.7820865974627469       (Google default Street View zoom)
//
// Schema decoded from Google's pb-encoded embed URL:
//   !1s<PANO_ID> !2m2 !1d<LNG> !2d<LAT> !3f<HEADING> !4f<PITCH> !5f<ZOOM>
const STREET_VIEW_EMBED =
  "https://www.google.com/maps/embed?pb=!4v1716077000000!6m8!1m7" +
  "!1sznkdVMmk13kaGn_VKMUTeQ" +
  "!2m2!1d139.665698!2d35.2822804" +
  "!3f135.94!4f-9.04!5f0.7820865974627469";

const FAQ = [
  {
    qEn: "How long does NoJo Enterprise usually take to respond?",
    qJa: "回答までの時間はどのくらいですか？",
    aEn: "Most messages are answered within 24 hours during business days. Urgent items go faster — call the office line for the fastest path.",
    aJa: "営業日であれば、ほとんどのお問い合わせには24時間以内にご返信しています。お急ぎの場合はお電話にてお問い合わせください。",
  },
  {
    qEn: "Which email should I use for English vs Japanese support?",
    qJa: "英語と日本語でメールアドレスは違いますか？",
    aEn: "Both addresses reach the same team. Pick whichever matches your preferred language — replies come back in that language.",
    aJa: "両方のメールアドレスは同じチームに届きます。お使いの言語に合わせてお選びください。返信もその言語でお送りします。",
  },
  {
    qEn: "Are you the same team as JapanETCcard or JapanTollReceipts?",
    qJa: "JapanETCcardやJapanTollReceiptsと同じ運営ですか？",
    aEn: "Yes — NoJo Enterprise is the corporate parent. Service-specific support is best handled by the relevant service desk; cross-service questions can come here.",
    aJa: "はい、NoJo Enterpriseがコーポレート運営元です。各サービス固有のお問い合わせは各サービスサイトの窓口へ、サービスを横断するご質問はこちらで承ります。",
  },
  {
    qEn: "Should I send sensitive information through this form?",
    qJa: "個人情報を送っても大丈夫ですか？",
    aEn: "No. Please do not send passwords, payment details, or sensitive credentials through this form. We will provide a secure channel if needed.",
    aJa: "本フォームでは、パスワード、お支払い情報、本人確認情報などの送信はお控えください。必要な場合は別途、安全な経路をご案内いたします。",
  },
];

/**
 * TOPIC_OPTIONS — self-routing select on the contact form.
 * The chosen value is posted as `topic` on /api/contact and the backend
 * prefixes it onto the staff-notification email subject so triage is
 * one glance. Stored verbatim — keep IDs stable across deploys.
 */
const TOPIC_OPTIONS = [
  { id: "japanetccard",      labelEn: "JapanETCcard support",        labelJa: "JapanETCcard サポート" },
  { id: "japantollreceipts", labelEn: "JapanTollReceipts support",   labelJa: "JapanTollReceipts サポート" },
  { id: "japanchangemoney",  labelEn: "JapanChangeMoney / preview",  labelJa: "JapanChangeMoney / プレビュー" },
  { id: "press",             labelEn: "Press / media",               labelJa: "報道・取材" },
  { id: "partnership",       labelEn: "Partnership / business",      labelJa: "提携・事業に関するお問い合わせ" },
  { id: "other",             labelEn: "Other / corporate",           labelJa: "その他 / コーポレート" },
];

/**
 * Contact page sections are defined below. The cross-service "Visit
 * other support desks" cards (formerly `SERVICE_QUICKLINKS`) were
 * removed on 2026-02-19 per owner directive — the corporate contact
 * page now focuses exclusively on NoJo Enterprise. Service-specific
 * support desks are still discoverable via the operated services pages
 * (`/services/...`) and the global footer.
 */

function pickL(en, ja, lang) {
  return lang === "ja" ? ja : en;
}

function FaqRow({ row, lang, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = `contact-faq-answer-${row.qEn.slice(0, 24).replace(/\s+/g, "-").toLowerCase()}`;
  return (
    <div className="border-b border-[#E2E8F0] py-4">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        data-testid={`contact-faq-toggle-${row.qEn.slice(0, 12).replace(/\s+/g, "-")}`}
        aria-expanded={open}
        aria-controls={panelId}
        className="w-full flex items-center justify-between gap-4 text-left"
      >
        <span className="font-display text-[15.5px] md:text-[17px] font-semibold tracking-tight text-[#0F1D2D]">
          {pickL(row.qEn, row.qJa, lang)}
        </span>
        <ChevronDown
          aria-hidden
          className={[
            "h-5 w-5 shrink-0 text-[#64748B] transition-transform",
            open ? "rotate-180" : "rotate-0",
          ].join(" ")}
          strokeWidth={2}
        />
      </button>
      {open && (
        <p
          id={panelId}
          role="region"
          className="mt-3 text-[14px] leading-[1.8] text-[#475569] max-w-3xl"
        >
          {pickL(row.aEn, row.aJa, lang)}
        </p>
      )}
    </div>
  );
}

export default function ContactPage({ lang }) {
  const isJa = lang === "ja";
  const location = useLocation();
  const [form, setForm] = useState({ name: "", email: "", message: "", topic: "" });
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Reset to a fresh empty form. Used by:
  //   • the "Send another message" button on the success card
  //   • the side-effect below that watches the URL key — so if a
  //     visitor is already on /contact and clicks the Contact button
  //     in the top nav (which re-navigates to /contact), the form
  //     resets to its empty state instead of staying stuck on the
  //     success card (owner directive 2026-05-21).
  const resetForm = () => {
    setForm({ name: "", email: "", message: "", topic: "" });
    setSent(false);
    setError(null);
    setSubmitting(false);
  };

  // Watch the URL key — React Router gives every navigation a unique
  // `location.key` even when the pathname is identical. So tapping
  // the Contact CTA while already on /contact flips `key`, which
  // triggers this effect, which clears the success state.
  useEffect(() => {
    resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Ref on the success card so we can scroll it into view after the
  // submission completes. Owner directive Feb 2026: "after it's done,
  // it needs to scroll to the top" — visitors weren't seeing the
  // success state because their scroll position was still at the
  // submit button at the bottom of the form.
  const successRef = useRef(null);

  useEffect(() => {
    if (!sent) return;
    // Defer one frame so the success card has actually mounted.
    const id = requestAnimationFrame(() => {
      if (successRef.current) {
        successRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    });
    return () => cancelAnimationFrame(id);
  }, [sent]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    // Client-side friendly validation mirroring server limits.
    const name = form.name.trim();
    const email = form.email.trim();
    const message = form.message.trim();
    if (name.length === 0) {
      setError(isJa ? "お名前を入力してください。" : "Please enter your name.");
      return;
    }
    if (name.length > 120) {
      setError(isJa ? "お名前は120文字以内でお願いします。" : "Name must be 120 characters or less.");
      return;
    }
    if (email.length === 0 || !email.includes("@")) {
      setError(isJa ? "正しいメールアドレスを入力してください。" : "Please enter a valid email address.");
      return;
    }
    if (message.length < 10) {
      setError(
        isJa
          ? "メッセージは10文字以上でお願いします。詳細を少し追記してください。"
          : "Your message is a bit short. Please share at least 10 characters so we can help."
      );
      return;
    }
    if (message.length > 5000) {
      setError(
        isJa
          ? "メッセージは5000文字以内でお願いします。長い内容は別途メールでお送りください。"
          : "Your message exceeds 5000 characters. Please send longer notes as a follow-up email."
      );
      return;
    }

    setSubmitting(true);
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${apiBase}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          message,
          topic: form.topic || null,
          language: lang,
          source_path: typeof window !== "undefined" ? window.location.pathname : null,
        }),
      });
      if (!res.ok) {
        let detail = isJa
          ? "送信に失敗しました。少し時間をおいて再度お試しください。"
          : "We couldn't send your message. Please try again in a moment.";
        try {
          const j = await res.json();
          // FastAPI 422 errors arrive as an array of field-level details.
          if (typeof j?.detail === "string") {
            detail = j.detail;
          } else if (Array.isArray(j?.detail) && j.detail.length > 0) {
            // Surface the first field-level message in a friendly form.
            const first = j.detail[0];
            const fieldPath = Array.isArray(first?.loc) ? first.loc.join(".") : "";
            const msg = first?.msg || "";
            if (fieldPath.includes("email")) {
              detail = isJa
                ? "メールアドレスの形式をご確認ください。"
                : "Please double-check the email address format.";
            } else if (fieldPath.includes("message")) {
              detail = isJa
                ? "メッセージの内容をご確認ください。"
                : "Please double-check the message content.";
            } else if (fieldPath.includes("name")) {
              detail = isJa
                ? "お名前の入力をご確認ください。"
                : "Please double-check the name field.";
            } else if (msg) {
              detail = msg;
            }
          }
        } catch {
          /* ignore */
        }
        throw new Error(detail);
      }
      // Success — submission persisted; staff notification dispatched.
      setSent(true);
    } catch (err) {
      setError(
        err?.message ||
          (isJa
            ? "ネットワークエラーが発生しました。"
            : "A network error occurred.")
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <article className="bg-white" data-testid="contact-page">
      <SEO {...PAGE_SEO.contact[lang]} path={`/${lang}/contact`} lang={lang} />
      <JsonLd
        data={breadcrumbSchema([
          { name: isJa ? "ホーム" : "Home", url: `/${lang}` },
          { name: isJa ? "お問い合わせ" : "Contact", url: `/${lang}/contact` },
        ])}
      />

      {/* ===== HEADER ===== */}
      <section className="border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-8 md:py-12">
          <Reveal as="div" variant="up">
            <h1
              data-testid="contact-page-h1"
              className={[
                isJa ? "font-ja" : "font-display",
                "text-[#0F1D2D] text-3xl md:text-[44px] font-bold tracking-tighter leading-[1.05]",
              ].join(" ")}
            >
              {isJa ? "NoJo Enterpriseへのお問い合わせ" : "Contact NoJo Enterprise"}
            </h1>
            <p
              className="mt-4 text-[14.5px] md:text-[16px] leading-[1.8] text-[#475569] max-w-3xl"
              data-testid="contact-page-subhead"
            >
              {isJa
                ? "法人・サービス・提携・サポートに関するお問い合わせは、横須賀のチームが日本語・英語で対応します。通常、1営業日以内に返信いたします。"
                : "Reach our Yokosuka-based team for corporate, service, partnership, or support inquiries. We respond in English and Japanese, typically within one business day."}
            </p>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          TWO-COLUMN — INFO LEFT / FORM RIGHT
          The cross-service "Visit other support desks" cards were removed
          per owner directive (2026-02-19): the corporate contact page
          focuses exclusively on NoJo Enterprise. Service-specific support
          desks are still discoverable via the operated services pages
          (`/services/...`) and the global footer.
          ═══════════════════════════════════════════════════════════════ */}
      <section className="bg-[#F8F9FA] border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-8 md:py-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            {/* LEFT — info blocks */}
            <div className="lg:col-span-5">
              <h2
                className={[
                  isJa ? "font-ja" : "font-display",
                  "text-[#0F1D2D] text-xl md:text-[24px] font-bold tracking-tight leading-tight mb-5",
                ].join(" ")}
              >
                {isJa ? "連絡先のご案内" : "Get in Touch"}
              </h2>

              <ul className="space-y-6">
                {/* Phone */}
                <InfoBlock
                  icon={<Phone className="h-5 w-5" strokeWidth={1.8} />}
                  accent={PURPLE}
                  testId="contact-info-phone"
                  label={isJa ? "電話" : "Phone"}
                >
                  <a
                    href="tel:+81468747156"
                    data-testid="contact-phone-link"
                    className="text-[18px] font-bold text-[#0F1D2D] hover:text-[#5B2E83] transition-colors"
                  >
                    046-874-7156
                  </a>
                  <div className="text-[12.5px] text-[#64748B] mt-1.5">
                    {isJa ? "国際 (+81) 46-874-7156" : "International: (+81) 46-874-7156"}
                  </div>
                  <div className="text-[12.5px] text-[#64748B]">
                    FAX: 046-874-7156
                  </div>
                  <div className="text-[12px] text-[#94A3B8] mt-2">
                    {isJa ? "営業時間内（JST）" : "Available during business hours (JST)"}
                  </div>
                </InfoBlock>

                {/* Email */}
                <InfoBlock
                  icon={<Mail className="h-5 w-5" strokeWidth={1.8} />}
                  accent={GREEN}
                  testId="contact-info-email"
                  label={isJa ? "メール" : "Email"}
                >
                  <a
                    href="mailto:support@nojoenterprise.com"
                    data-testid="contact-email-link"
                    className="block text-[15px] font-semibold text-[#0F1D2D] hover:text-[#2F8F3C] transition-colors break-words"
                  >
                    support@nojoenterprise.com
                  </a>
                  <div className="text-[11.5px] font-medium text-[#64748B] mt-1">
                    {isJa ? "（日本語・英語サポート）" : "(English & Japanese support)"}
                  </div>
                  <p className="mt-3 text-[12px] leading-[1.7] text-[#94A3B8]">
                    {isJa
                      ? "お送りいただいたメッセージはすべて同じサポートチームに届きます。ご使用の言語で返信いたします。通常24時間以内にご返信いたします。"
                      : "All messages reach the same support team. We'll reply in the language you write in — typically within 24 hours."}
                  </p>
                </InfoBlock>

                {/* Office */}
                <InfoBlock
                  icon={<MapPin className="h-5 w-5" strokeWidth={1.8} />}
                  accent={BLUE}
                  testId="contact-info-office"
                  label={isJa ? "本社所在地" : "Office Location"}
                >
                  <div className="text-[15px] font-bold text-[#0F1D2D]">
                    {pickL(OFFICE.nameEn, OFFICE.nameJa, lang)}
                  </div>
                  <div className="text-[13.5px] text-[#475569] mt-1.5 leading-[1.6]">
                    {pickL(OFFICE.addr1En, OFFICE.addr1Ja, lang)}
                  </div>
                  <div className="text-[13.5px] text-[#475569] leading-[1.6]">
                    {pickL(OFFICE.addr2En, OFFICE.addr2Ja, lang)}
                  </div>
                  <a
                    href={MAPS_LINK}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-maps-link"
                    className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-semibold tracking-tight hover:opacity-80"
                    style={{ color: BLUE }}
                  >
                    {isJa ? "Google Mapsで見る" : "View on Google Maps"}
                    <ArrowUpRight className="h-4 w-4" strokeWidth={2.25} />
                  </a>
                </InfoBlock>

                {/* Hours */}
                <InfoBlock
                  icon={<Clock className="h-5 w-5" strokeWidth={1.8} />}
                  accent="#0F1D2D"
                  testId="contact-info-hours"
                  label={isJa ? "営業時間" : "Business Hours"}
                >
                  <div className="text-[14px] text-[#0F1D2D]">
                    <span className="font-semibold">
                      {isJa ? "月曜 - 金曜" : "Monday – Friday"}:
                    </span>{" "}
                    9:00 – 17:00 (JST)
                  </div>
                  <div className="text-[14px] text-[#475569] mt-1">
                    <span className="font-semibold">
                      {isJa ? "土曜・日曜" : "Saturday & Sunday"}:
                    </span>{" "}
                    {isJa ? "休業" : "Closed"}
                  </div>
                  <p className="mt-3 text-[12px] leading-[1.7] text-[#94A3B8]">
                    {isJa
                      ? "日本の祝日および年末年始は休業となります（事前にご案内いたします）。"
                      : "Closed on Japanese national holidays and select holiday periods (announced in advance)."}
                  </p>
                </InfoBlock>
              </ul>
            </div>

            {/* RIGHT — form */}
            <div className="lg:col-span-7">
              <div className="bg-white rounded-2xl ring-1 ring-[#E2E8F0] p-6 md:p-7">
                <h2
                  className={[
                    isJa ? "font-ja" : "font-display",
                    "text-[#0F1D2D] text-xl md:text-[24px] font-bold tracking-tight leading-tight mb-5",
                  ].join(" ")}
                >
                  {isJa ? "メッセージを送る" : "Send Us a Message"}
                </h2>

                {sent ? (
                  <div
                    ref={successRef}
                    role="status"
                    aria-live="polite"
                    className="rounded-xl p-6 ring-1 ring-[#BBF7D0] scroll-mt-24"
                    style={{ backgroundColor: "#F0FDF4" }}
                    data-testid="contact-form-success"
                    tabIndex={-1}
                  >
                    <CheckCircle2 className="h-7 w-7 text-[#16A34A] mb-3" strokeWidth={2} />
                    <h3 className="font-display text-[22px] font-bold text-[#0F1D2D]">
                      {isJa ? "お問い合わせを受け付けました。" : "We've got your message."}
                    </h3>
                    <p className="mt-2 text-[14px] leading-[1.7] text-[#475569]">
                      {isJa
                        ? "営業日であれば、通常24時間以内にご返信いたします。お急ぎの場合はお電話 (046-874-7156) までご連絡ください。"
                        : "We typically reply within 24 hours on business days. Need it faster? Call us at 046-874-7156."}
                    </p>
                    <button
                      type="button"
                      onClick={resetForm}
                      data-testid="contact-form-send-another"
                      className="mt-5 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#5B2E83] text-white text-[13px] font-semibold tracking-tight hover:bg-[#4A2570] active:bg-[#4A2570] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5B2E83] focus-visible:ring-offset-2 touch-manipulation"
                    >
                      <RotateCcw className="h-4 w-4" strokeWidth={2} />
                      {isJa ? "別のメッセージを送る" : "Send another message"}
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                    {/* Topic self-routing select — optional but encouraged.
                        Sent to backend as `topic`; staff email subject gets
                        the prefix [<topic>] so triage is one glance. */}
                    <div>
                      <label
                        htmlFor="contact-topic"
                        className="block text-[12.5px] tracking-[0.04em] font-semibold text-[#0F1D2D] mb-1.5"
                      >
                        {isJa ? "お問い合わせの種類" : "What's this about?"}
                        <span className="ml-1 text-[#94A3B8] font-normal">
                          {isJa ? "(任意)" : "(optional)"}
                        </span>
                      </label>
                      <div className="relative">
                        <select
                          id="contact-topic"
                          data-testid="contact-form-field-topic"
                          value={form.topic}
                          onChange={(e) => setForm({ ...form, topic: e.target.value })}
                          className="w-full appearance-none rounded-lg ring-1 ring-[#E2E8F0] bg-white pl-3.5 pr-10 h-11 text-[14px] text-[#0F1D2D] focus:outline-none focus:ring-2 focus:ring-[#5B2E83] transition-shadow"
                        >
                          <option value="">
                            {isJa ? "選択してください" : "Select a topic"}
                          </option>
                          {TOPIC_OPTIONS.map((o) => (
                            <option key={o.id} value={o.id}>
                              {isJa ? o.labelJa : o.labelEn}
                            </option>
                          ))}
                        </select>
                        <ChevronDown
                          aria-hidden
                          className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]"
                          strokeWidth={2}
                        />
                      </div>
                    </div>

                    <Field
                      id="name"
                      label={isJa ? "お名前" : "Your Name"}
                      required
                      value={form.name}
                      onChange={(v) => setForm({ ...form, name: v })}
                      type="text"
                    />
                    <Field
                      id="email"
                      label={isJa ? "メールアドレス" : "Email Address"}
                      required
                      value={form.email}
                      onChange={(v) => setForm({ ...form, email: v })}
                      type="email"
                    />
                    <Field
                      id="message"
                      label={isJa ? "メッセージ" : "Message"}
                      required
                      value={form.message}
                      onChange={(v) => setForm({ ...form, message: v })}
                      multiline
                    />

                    <p className="text-[12px] leading-[1.7] text-[#94A3B8] flex items-start gap-2 bg-[#F8F9FA] rounded-lg px-3 py-2.5">
                      <span aria-hidden className="text-[#F59E0B] mt-0.5">⚠</span>
                      {isJa
                        ? "パスワード、お支払い情報、本人確認情報などの送信はお控えください。"
                        : "Please do not send passwords, payment details, or sensitive credentials through this form."}
                    </p>

                    {error && (
                      <p
                        data-testid="contact-form-error"
                        role="alert"
                        className="text-[12.5px] leading-[1.7] text-[#B91C1C] bg-[#FEF2F2] ring-1 ring-[#FECACA] rounded-lg px-3 py-2.5"
                      >
                        {error}
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="contact-form-submit"
                      className="inline-flex items-center gap-2 text-white px-6 h-12 rounded-full text-[14px] font-semibold tracking-tight transition-all hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
                      style={{ backgroundColor: PURPLE }}
                    >
                      {submitting
                        ? isJa
                          ? "送信中…"
                          : "Sending…"
                        : isJa
                          ? "メッセージを送信"
                          : "Send Message"}
                      {!submitting && <Send className="h-4 w-4" strokeWidth={2.25} />}
                    </button>

                    <p className="text-[12.5px] text-[#475569]">
                      {isJa
                        ? "お急ぎの場合は、046-874-7156 までお電話ください。"
                        : "Need immediate assistance? Call us at 046-874-7156 for faster support."}
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== LOCATION / GOOGLE MAP ===== */}
      <section
        id="office"
        className="relative isolate bg-white border-b border-[#E2E8F0] scroll-mt-24"
      >
        <PageSideAura testId="contact-location-aura" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-12 md:pt-16 pb-10 md:pb-14">
          <div className="flex items-end justify-between gap-4 mb-10 md:mb-12 flex-wrap">
            <div>
              <div className="text-[10px] tracking-[0.28em] font-mono-display uppercase font-bold mb-2" style={{ color: BLUE }}>
                {isJa ? "本社所在地" : "Our Location"}
              </div>
              <h2 className={[isJa ? "font-ja" : "font-display", "text-[#0F1D2D] text-2xl md:text-[36px] font-bold tracking-tight leading-tight"].join(" ")}>
                {isJa ? "横須賀・本町の本社" : "Visit us in Yokosuka, Honcho."}
              </h2>
            </div>
            <a
              href={MAPS_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-maps-open"
              className="inline-flex items-center gap-1.5 px-4 h-11 rounded-full text-[13px] font-semibold tracking-tight ring-1 hover:bg-[#F1F5F9] transition-colors"
              style={{ color: BLUE, borderColor: BLUE, borderWidth: 1 }}
            >
              {isJa ? "Google Mapsで開く" : "Open in Google Maps"}
              <ArrowUpRight className="h-4 w-4" strokeWidth={2.25} />
            </a>
          </div>

          <div
            className="relative w-full overflow-hidden rounded-2xl ring-1 ring-[#E2E8F0] bg-[#F1F5F9]"
            style={{ aspectRatio: "21 / 9" }}
            data-testid="contact-map-frame"
          >
            <iframe
              title={isJa ? "NoJo Enterprise本社・ストリートビュー" : "NoJo Enterprise HQ — Street View"}
              src={STREET_VIEW_EMBED}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="absolute inset-0 w-full h-full border-0"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="bg-[#F8F9FA]">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-10 md:py-14">
          <div className="text-[10px] tracking-[0.28em] font-mono-display uppercase font-bold mb-2 text-[#64748B]">
            {isJa ? "よくあるご質問" : "Most asked questions"}
          </div>
          <h2 className={[isJa ? "font-ja" : "font-display", "text-[#0F1D2D] text-2xl md:text-[36px] font-bold tracking-tight leading-tight"].join(" ")}>
            {isJa
              ? "メッセージを書く前に — よくいただくご質問。"
              : "Before you write — the questions people ask most."}
          </h2>

          <div className="mt-8" data-testid="contact-faq-list">
            {FAQ.map((row, i) => (
              <FaqRow key={i} row={row} lang={lang} defaultOpen={i === 0} />
            ))}
          </div>
        </div>
      </section>
    </article>
  );
}

/* ----- helpers ----- */

function InfoBlock({ icon, accent, label, children, testId }) {
  return (
    <li className="flex items-start gap-4" data-testid={testId}>
      <span
        className="inline-flex items-center justify-center h-11 w-11 rounded-xl shrink-0"
        style={{ backgroundColor: `${accent}15`, color: accent }}
      >
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] tracking-[0.22em] font-mono-display uppercase font-bold mb-1.5" style={{ color: accent }}>
          {label}
        </div>
        {children}
      </div>
    </li>
  );
}

function Field({ id, label, value, onChange, required, type = "text", multiline = false }) {
  const baseInput =
    "w-full rounded-lg px-4 py-3 bg-white border border-[#E2E8F0] text-[14.5px] text-[#0F1D2D] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#5B2E83]/40 focus:border-[#5B2E83] transition-shadow";
  return (
    <label htmlFor={id} className="block">
      <span className="block text-[13px] font-semibold text-[#0F1D2D] mb-1.5">
        {label} {required && <span className="text-[#DC2626]">*</span>}
      </span>
      {multiline ? (
        <textarea
          id={id}
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={5}
          data-testid={`contact-form-field-${id}`}
          className={`${baseInput} resize-y min-h-[140px]`}
        />
      ) : (
        <input
          id={id}
          required={required}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`contact-form-field-${id}`}
          className={baseInput}
        />
      )}
    </label>
  );
}
